import { mergeRefs } from "@react-aria/utils";
import cx from "classnames";
import { useCallback, useState } from "react";

import { ChevronDown } from "components/icons/generated";
import { LABEL_FOLDER_KEY } from "constants/labels";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownMenu from "ds/components/DropdownMenu";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tag from "ds/components/Tag";
import Tooltip from "ds/components/Tooltip";
import useElementWidth from "hooks/useElementWidth";
import useOverflowItems from "hooks/useOverflowItems";

import TagsListFilterableNewSingleTag from "./SingleTag";
import styles from "./styles.module.css";

const SPACE = 4;
const INITIAL_MULTIPLE_TAGS_DROPDOWN_WIDTH = 90;

export type TagsListFilterableProps = {
  applyFolderFilter?: (value: string) => void;
  applyLabelFilter?: (value: string) => void;
  tags: string[];
  withAutoAttach?: boolean;
};

const TagsListFilterableNew = ({
  tags,
  applyFolderFilter,
  applyLabelFilter,
  withAutoAttach = false,
}: TagsListFilterableProps) => {
  const [tagsSizes, setTagSizes] = useState<Record<string, number>>({});

  const [containerRef, containerSize] = useElementWidth<HTMLDivElement>();
  const [multipleTagsDropdownRef, multipleTagsDropdownSize] = useElementWidth<HTMLButtonElement>(
    INITIAL_MULTIPLE_TAGS_DROPDOWN_WIDTH
  );

  const hasTagAction = !!applyLabelFilter || !!applyFolderFilter;

  const getItemWidth = useCallback((tag: string) => tagsSizes[tag] || 0, [tagsSizes]);

  const { hiddenItems: hiddenTags } = useOverflowItems({
    items: tags,
    containerWidth: containerSize,
    getItemWidth,
    overflowTriggerWidth: multipleTagsDropdownSize,
    gap: SPACE,
  });

  const tagOnClickHandler = useCallback(
    (tag: string, callback?: () => void) => {
      const isFolder = tag.startsWith(LABEL_FOLDER_KEY);

      if (isFolder && applyFolderFilter) {
        applyFolderFilter(tag.replace(LABEL_FOLDER_KEY, ""));
      } else {
        applyLabelFilter?.(tag);
      }

      callback?.();
    },
    [applyFolderFilter, applyLabelFilter]
  );

  if (tags.length === 0) {
    return null;
  }

  if (tags.length === 1) {
    return (
      <Tooltip
        on={({ ref, ...props }) => (
          <Tag
            {...props}
            ref={ref}
            className={styles.tag}
            tag={tags[0]}
            onClick={hasTagAction ? () => tagOnClickHandler(tags[0]) : undefined}
            withAutoAttach={withAutoAttach}
          />
        )}
        active={hasTagAction}
      >
        Add to filters
      </Tooltip>
    );
  }

  const moreTagsTooltip = `${hiddenTags.length === tags.length ? `${hiddenTags.length} labels` : `+${hiddenTags.length}`}`;

  return (
    <Box
      ref={containerRef}
      className={styles.container}
      fullWidth
      gap="small"
      justify="start"
      align="center"
    >
      {tags.map((tag) => (
        <TagsListFilterableNewSingleTag
          setTagSizes={setTagSizes}
          currentSize={tagsSizes[tag]}
          tagOnClickHandler={hasTagAction ? tagOnClickHandler : undefined}
          shouldHide={hiddenTags.includes(tag)}
          key={tag}
          tag={tag}
          containerSize={containerSize}
          withAutoAttach={withAutoAttach}
          activeTooltip={hasTagAction}
        />
      ))}
      {!!hiddenTags.length && (
        <DropdownMenu
          className={styles.dropdownSection}
          triggerComponent={
            <Tooltip
              on={({ ref, ...tooltipProps }) => (
                <Button
                  {...tooltipProps}
                  ref={mergeRefs<HTMLButtonElement>(ref, multipleTagsDropdownRef)}
                  aria-label="Show more tags"
                  className={cx(
                    styles.button,
                    !!containerSize && styles.active,
                    tooltipProps.className
                  )}
                  variant="secondary"
                  size="small"
                  endIcon={ChevronDown}
                  withTextEllipsis
                >
                  {moreTagsTooltip}
                </Button>
              )}
            >
              Show more labels
            </Tooltip>
          }
        >
          {hiddenTags.map((tag) => {
            return (
              <DropdownMenuItem
                tooltip="Add to filters"
                onAction={hasTagAction ? () => tagOnClickHandler(tag) : undefined}
                tooltipPlacement="right"
                isTooltipActive={hasTagAction}
                key={tag}
              >
                <Tag className={styles.tag} tag={tag} withAutoAttach={withAutoAttach} />
              </DropdownMenuItem>
            );
          })}
        </DropdownMenu>
      )}
    </Box>
  );
};

export default TagsListFilterableNew;
